import { Header, Link, Table, TableColumn } from '@backstage/core-components';
import { discoveryApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button, makeStyles, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import Loader from '../loader/Loader';
import { useNavigate } from 'react-router';

const useStyles = makeStyles({
  container: {
    padding: '40px',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '30px',
    marginRight: '38px',
    marginLeft: '38px',
  },
});

export default function Terraform() {
  const containerStyle = useMemo(() => ({ width: '100%', height: '100vh' }), []);
  const discoveryApi = useApi(discoveryApiRef);
  const [workflows, setWorkflows] = useState<any[]>([]);
  const styles = useStyles();
  const [org, setOrg] = useState('');
  const navigate = useNavigate();
  const identityApi = useApi(identityApiRef);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('')

  const [tenants, setTenants] = useState<{ id: number; name: string }[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>('all'); 

  const formatData = (response: any) => {
    let listOfRepos = response.filter((item: any) => {
      return item.metadata.annotations['terraform.io/organization'];
    });
    const data: any[] = listOfRepos.map((item: any) => ({
      name: item.metadata.name,
      description: item.metadata.description,
      owner: item.spec.owner,
      service: item.spec.type,
      lifecycle: item.spec.lifecycle,
      entity: item,
    }));
    return data;
  };

  const fetchTenantsFromDb = async () => {
    try {
       
      const baseUrl = await discoveryApi.getBaseUrl('user');
      const response = await axios.get(`${baseUrl}/tenants`);
      setTenants(response.data);
      return response.data;
    } catch (e) {
      console.error('Error fetching tenants:', e);
      return [];
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const details = await identityApi.getBackstageIdentity();
      const tenantId = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('tenantId:')
      )?.split(':')[1];
      const userRole = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('role:')
      )?.split(':')[1];

      setRole(userRole)
      // fetch tenants from DB
      const tenantsFromDb = await fetchTenantsFromDb();
      const tenant = tenantsFromDb.find(t => t.id.toString() === tenantId);
      setOrg(tenant.name);


      const baseUrl = await discoveryApi.getBaseUrl('catalog');
      let response;
      if (userRole === 'SuperAdmin') {
        response = await axios.get(`${baseUrl}/entities?filter=kind=component`);
      } else {
        response = await axios.get(
          `${baseUrl}/entities?filter=kind=component,metadata.annotations.tenant.com/tenant-id=${tenantId}`
        );
      }

      let filteredResponse = response.data.filter((entity: any) => {
        let annotations = entity?.metadata?.annotations || {};
        return annotations['tenant.com/tenant-id'];
      });
    
      // enrich with tenantName
      const enrichedResponse = filteredResponse.map((entity: any) => {
        const annotations = entity?.metadata?.annotations || {};
        const entityTenantId = parseInt(annotations['tenant.com/tenant-id'], 10);

        const matchedTenant = tenantsFromDb.find((t: any) => t.id === entityTenantId);

        return {
          ...entity,
          tenantName: matchedTenant ? matchedTenant.name : 'Unknown',
        };
      });

      // unique tenants
      const uniqueTenants = Array.from(
        new Map(
          filteredResponse.map((entity: any) => {
            const id = parseInt(entity.metadata.annotations['tenant.com/tenant-id'], 10);
            return [id, { id, name: entity.tenantName }];
          })
        ).values()
      );

      
      let listOfWorkflows = formatData(enrichedResponse);
      setWorkflows(listOfWorkflows);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleButtonClick = () => {
    navigate('/custom-create');
  };

  const handleTenantChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const tenantId = event.target.value as string;
    setSelectedTenant(tenantId);

    // if (tenantId === 'all') {
    //   setOrg('All Tenants');
    // } else {
    //   const tenant = tenants.find(t => t.id.toString() === tenantId);
    //   if (tenant) {
    //     setOrg(tenant.name);
    //   }
    // }
  };

  const columns: TableColumn[] = [
    {
      title: 'Name',
      field: 'name',
      highlight: true,
      render: ({ entity }) => (
        <Link to={`/catalog/default/component/${entity?.metadata?.name}`}>
          {entity?.metadata?.name}
        </Link>
      ),
    },
    { title: 'Description', field: 'description' },
    { title: 'Owner', field: 'owner' },
    { title: 'Service', field: 'service' },
    { title: 'Lifecycle', field: 'lifecycle' },
  ];

  // filter workflows
  const filteredWorkflows =
    selectedTenant === 'all'
      ? workflows
      : workflows.filter(
        wf => wf.entity.metadata.annotations['tenant.com/tenant-id'] === selectedTenant
      );

  return (
    <div style={containerStyle}>
      <Header title={'Terraform'} subtitle={org} />

      <div className={styles.buttonContainer}>
        <div>
          <Button variant="contained" color="primary" onClick={handleButtonClick}>
            Create
          </Button>
        </div>
        {role === "SuperAdmin" && (
          <div>
            <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
              <InputLabel id="tenant-select-label"></InputLabel>
              <Select
                labelId="tenant-select-label"
                value={selectedTenant}
                onChange={handleTenantChange}
                displayEmpty
              >
                {/*  Default All Tenants option */}
                <MenuItem value="all">All Tenants</MenuItem>
                {tenants.map(tenant => (
                  <MenuItem key={tenant.id} value={tenant.id.toString()}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}
      </div>

      <div className={styles.container}>
        {loading ? (
          <Loader />
        ) : filteredWorkflows.length > 0 ? (
          <Table
            options={{ paging: false }}
            data={filteredWorkflows}
            columns={columns}
            title="Terraform Components"
          />
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              fontSize: '20px',
            }}
          >
            No Terraform Components found.
          </div>
        )}
      </div>
    </div>
  );
}
